import { useMutation, useQuery } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { Box, Building2Icon, ChevronRightIcon, Download, FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { type DataUpload, DataUploadType } from '@/common/data-upload';
import type { SubAsset } from '@/common/sub-asset';
import type { WithDeletedBy } from '@/common/soft-delete';
import { DeleteButton } from '@/components/DeleteButton';
import { DeleteConfirmDialog } from '@/components/soft-delete/DeleteConfirmDialog';
import { RestoreItemButton } from '@/components/soft-delete/RestoreItemButton';
import { DeletedItemBanner } from '@/components/soft-delete/DeletedItemBanner';
import { ReadOnlySubtree } from '@/context/AssetReadOnlyContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatDateOnly } from '@/lib/utils';
import { buildSubAssetPath, getTreeView } from '@/lib/utils/asset';
import {
	fileTypeLabel,
	getDataUploadTypeIconConfig,
	isCeisiumIdDataUpload,
	isImageFileDataUpload,
	isPdfFileDataUpload,
} from '@/lib/utils/data-upload';
import { getAssetOptions } from '@/queries/assets/get-asset';
import { getDataUploadPresignedUrlOptions } from '@/queries/data-uploads/get-data-upload-presigned-url';
import { updateDataUpload } from '@/queries/data-uploads/update-data-upload';
import { queryClient } from '@/queries/query.client';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { ROLE } from '../../../common/role';
import { usePermission } from '../../../hooks/use-permission';
import { useSoftDeleteItem } from '../../../hooks/use-soft-delete';
import { ImageWithFallback } from '../../imageWithFallback';
import { Restricted } from '../../Restricted';
import { SubAssetTreeSelector } from '../../sub-asset/sub-asset-selector/SubAssetTreeSelector';
import { Switch } from '../../ui/switch';

export type DataUploadPreviewEditModalProps = {
	dataUpload: DataUpload | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	subAssets?: SubAsset[];
	assetId?: string;
	doRefresh?: () => void;
	/** A deleted file from the deleted list: read-only, with a warning and Restore; it can still be viewed and downloaded (GH #331) */
	deleted?: boolean;
};
export function DataUploadPreviewEditModal({
	dataUpload,
	onOpenChange,
	open,
	subAssets,
	assetId,
	doRefresh,
	deleted = false,
}: DataUploadPreviewEditModalProps) {
	const { t } = useTranslation();
	const { icon: PreviewIcon, color: previewIconColor } = (dataUpload && getDataUploadTypeIconConfig(dataUpload)) || {};
	const [showDeleteAlert, setShowDeleteAlert] = useState(false);
	const [isAssetTreeOpen, setIsAssetTreeOpen] = useState(false);
	const [selectedAssetStructureId, setSelectedAssetStructureId] = useState<string>('');
	const [formData, setFormData] = useState({
		cesiumId: dataUpload?.cesiumId || '',
		name: dataUpload?.name || '',
		subSurfaceMode: dataUpload?.subSurfaceMode || false,
	});
	const shouldUpdateDataUploads = usePermission(ROLE.DATA_UPLOADS_UPDATE) && !deleted;

	// When modal opens/closes or dataUpload changes, reset form state
	useEffect(() => {
		if (!open) {
			setIsAssetTreeOpen(false);
		}
		if (open) {
			setFormData({
				cesiumId: dataUpload?.cesiumId || '',
				name: dataUpload?.name || '',
				subSurfaceMode: dataUpload?.subSurfaceMode || false,
			});
			setSelectedAssetStructureId(dataUpload?.subAssetId || assetId || '');
		}
	}, [open, dataUpload?.cesiumId, dataUpload?.name, dataUpload?.subAssetId, dataUpload?.subSurfaceMode, assetId]);

	const { data: asset } = useQuery(getAssetOptions(assetId || ''));

	// Fetch a short-lived presigned GET URL for file-type uploads (private bucket).
	const { data: presignedUrlData } = useQuery(
		getDataUploadPresignedUrlOptions(
			open && dataUpload?.uploadType === DataUploadType.FILE && dataUpload?.fileUrl ? dataUpload.id : null,
			{ deleted },
		),
	);
	const updateDataUploadApi = useMutation({
		mutationFn: async ({ id, data }: { id: string; data: DataUpload }) => {
			// Call update API here
			return updateDataUpload(id as string, {
				name: data.name as string,
				fileUrl: data.fileUrl as string,
				subAssetId: data.subAssetId || null,
				cesiumId: data.cesiumId || '',
				subSurfaceMode: data.subSurfaceMode || false,
			});
		},
		onSuccess: async () => {
			// Handle success (e.g., show notification, refresh data)
			toast.success(t('files.toast.updated'));
			await queryClient.invalidateQueries({ queryKey: ['data-uploads', 'list'] });
			doRefresh?.();
			onOpenChange(false);
		},
		onError: (error) => {
			// Handle error (e.g., show error notification)
			toast.error(t('files.toast.updateFailed', { message: error.message }));
		},
	});

	// Soft delete (GH #331): the dialog shows whether something blocks it (a running conversion);
	// the toast offers Undo. Broad invalidation so the main map's tile loader also drops the tileset.
	const deleteDataUploadApi = useSoftDeleteItem({
		onDeleted: () => {
			setShowDeleteAlert(false);
			doRefresh?.();
			onOpenChange(false);
		},
	});

	const subAssetsWithFirstParent = useMemo((): SubAsset[] => {
		// If no sub-assets, return empty array
		if (!subAssets?.length || !asset?.id) return [];

		// Use getTreeView to structure sub-assets hierarchically
		const children = getTreeView(subAssets);
		return [
			{
				id: asset?.id as string,
				name: asset?.name || '',
				parentSubAssetId: null,
				children,
			},
		];
	}, [subAssets, asset?.id, asset?.name]);

	const getAssetBreadcrumbPath = (assetId: string): string => {
		const path = buildSubAssetPath(subAssetsWithFirstParent, assetId);

		return path.map((s) => s.name || '').join(' > ');
	};

	const onSave = () => {
		if (!dataUpload || !formData.name) return;

		if (dataUpload.uploadType === DataUploadType.CESIUM_ID && !dataUpload.name) {
			toast.error(t('files.validation.nameRequiredForCesium'));
			return;
		}
		if (dataUpload.uploadType === DataUploadType.CESIUM_ID && !dataUpload.cesiumId) {
			toast.error(t('files.validation.cesiumIdRequiredForUpdate'));
			return;
		}

		const uploadData: Partial<DataUpload> = {
			subAssetId: selectedAssetStructureId && selectedAssetStructureId !== assetId ? selectedAssetStructureId : null,
			name: formData.name,
		};
		if (dataUpload.uploadType === DataUploadType.CESIUM_ID) {
			uploadData.cesiumId = formData.cesiumId;
			uploadData.subSurfaceMode = formData.subSurfaceMode || false;
		}

		updateDataUploadApi.mutate({ id: dataUpload.id as string, data: uploadData });
	};

	const fileName = dataUpload?.name || t('common.entities.file', { count: 1 });
	const onDelete = () => {
		if (!dataUpload) return;
		deleteDataUploadApi.mutate({
			target: softDeleteTargets.dataUpload(dataUpload.id as string),
			name: fileName,
			successMessage: t('files.toast.deleted'),
		});
	};

	const doDownload = () => {
		if (!dataUpload) return;
		if (isCeisiumIdDataUpload(dataUpload)) {
			toast.error(t('files.toast.downloadNotAvailable'));
			return;
		}
		if (!presignedUrlData?.presignedUrl) {
			toast.error(t('files.toast.downloadUrlNotReady'));
			return;
		}
		saveAs(presignedUrlData.presignedUrl, dataUpload.name as string);
	};

	const onSubSurfaceModeChange = (checked: boolean) => {
		setFormData((prev) => ({
			...prev,
			subSurfaceMode: checked,
		}));
	};

	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};
	const filesizeInMB = dataUpload?.fileSizeInKB ? (dataUpload.fileSizeInKB / 1024).toFixed(2) : 0;

	const deletedUpload = deleted ? (dataUpload as WithDeletedBy<DataUpload> | null) : null;
	return (
		<ReadOnlySubtree active={deleted}>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-3xl">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							{dataUpload && PreviewIcon && <PreviewIcon className={previewIconColor} />}
							{dataUpload?.name}
						</DialogTitle>
						{deletedUpload?.id && (
							<DeletedItemBanner
								itemLabel={t('common.entities.file', { count: 1 })}
								deletedAt={deletedUpload.deletedAt}
								deletedBy={deletedUpload.deletedBy}
							/>
						)}
					</DialogHeader>

					<div className="space-y-4 py-4">
						{/* File Preview */}
						{dataUpload && isImageFileDataUpload(dataUpload) && (
							<div className="bg-card border rounded-lg overflow-hidden">
								<ImageWithFallback
									src={presignedUrlData?.presignedUrl as string}
									alt={dataUpload.name as string}
									className="w-full h-auto max-h-100 object-contain"
								/>
							</div>
						)}

						{dataUpload && isPdfFileDataUpload(dataUpload) && (
							<div className="bg-card border rounded-lg p-8 text-center">
								<FileText className="h-16 w-16 mx-auto mb-4 text-red-600" />
								<p className="mb-2">{t('files.preview.pdfDocument')}</p>
							</div>
						)}

						{dataUpload && isCeisiumIdDataUpload(dataUpload) && (
							<div className="bg-card border rounded-lg p-8 text-center">
								<Box className="h-16 w-16 mx-auto mb-4 text-purple-600" />
								<p className="mb-2">{t('files.preview.cesiumModel')}</p>
							</div>
						)}

						{/* Documents without an inline preview (Excel, CSV, text, Word) */}
						{dataUpload &&
							dataUpload.uploadType === DataUploadType.FILE &&
							!isImageFileDataUpload(dataUpload) &&
							!isPdfFileDataUpload(dataUpload) && (
								<div className="bg-card border rounded-lg p-8 text-center">
									<FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
									<p className="mb-2">{fileTypeLabel(t, dataUpload.fileType)}</p>
									<p className="text-xs text-muted-foreground">{t('files.preview.noPreview')}</p>
								</div>
							)}

						{/* File Details */}
						<div className="bg-card border rounded-lg p-4 space-y-3">
							{dataUpload?.uploadType === DataUploadType.FILE && (
								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label className="text-xs text-muted-foreground font-semibold uppercase">
											{t('files.preview.fileSize')}
										</Label>
										<p className="text-sm">{filesizeInMB} MB</p>
									</div>
									<div>
										<Label className="text-xs text-muted-foreground font-semibold uppercase">
											{t('files.preview.uploaded')}
										</Label>
										<p className="text-sm">{formatDateOnly(dataUpload.createdAt as string)}</p>
									</div>
								</div>
							)}

							<div>
								<Label className="text-sm font-medium text-muted-foreground mb-2 block">
									{t('files.upload.fileNameLabel')} <span className="text-red-500">*</span>
								</Label>
								<Input
									type="text"
									className="bg-input"
									placeholder={t('files.upload.fileNamePlaceholder')}
									name="name"
									value={formData.name}
									onChange={onChangeHandler}
									disabled={!shouldUpdateDataUploads}
								/>
							</div>
							{dataUpload?.uploadType === DataUploadType.CESIUM_ID && (
								<div>
									<Label className="text-sm font-medium text-muted-foreground mb-2 block">
										{t('files.upload.cesiumIdLabel')}
									</Label>
									<Input
										type="text"
										className="bg-input"
										placeholder={t('files.upload.cesiumIdPlaceholder')}
										name="cesiumId"
										value={formData.cesiumId}
										onChange={onChangeHandler}
										disabled={!shouldUpdateDataUploads}
									/>
								</div>
							)}
							<div>
								<Label className="text-xs font-semibold text-muted-foreground uppercase mb-2 block">
									{t('files.upload.assetStructureLabel')} <span className="text-red-500">*</span>
								</Label>
								<div className="space-y-2">
									<Button
										type="button"
										variant="outline"
										className="bg-input rounded p-3 py-5 cursor-pointer transition-colors w-full justify-start"
										onClick={() => setIsAssetTreeOpen(!isAssetTreeOpen)}
										disabled={!shouldUpdateDataUploads}
									>
										<div className="flex items-center justify-between w-full">
											<div className="flex items-center gap-2">
												<Building2Icon className="h-4 w-4" />
												<span className="text-sm">
													{selectedAssetStructureId
														? getAssetBreadcrumbPath(selectedAssetStructureId)
														: t('files.upload.selectAssetPlaceholder')}
												</span>
											</div>
											<ChevronRightIcon
												className={cn('h-4 w-4 transition-transform', isAssetTreeOpen && 'rotate-90')}
											/>
										</div>
									</Button>

									{isAssetTreeOpen && (
										<SubAssetTreeSelector
											selectedId={selectedAssetStructureId}
											onSelect={(id: string) => {
												setSelectedAssetStructureId(id);
												setIsAssetTreeOpen(false);
											}}
											subAssets={subAssetsWithFirstParent}
										/>
									)}
								</div>
							</div>
							{dataUpload?.uploadType === DataUploadType.CESIUM_ID && (
								<div className="flex flex-row items-center gap-4">
									<Label htmlFor="subSurfaceMode" className="text-sm font-medium text-muted-foreground mb-2 block">
										{t('files.upload.subSurfaceModeLabel')}
									</Label>
									<Switch
										name="subSurfaceMode"
										id={'subSurfaceMode'}
										checked={formData.subSurfaceMode}
										onCheckedChange={onSubSurfaceModeChange}
										disabled={!shouldUpdateDataUploads}
									/>
								</div>
							)}
						</div>
					</div>

					<DialogFooter className="flex flex-row! justify-between items-center gap-2 flex-wrap">
						<div className="flex gap-2">
							{dataUpload?.uploadType === DataUploadType.FILE && (
								<Button
									variant="outline"
									size="sm"
									className="bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"
									onClick={doDownload}
								>
									<Download className="h-4 w-4" />
									{t('common.actions.download')}
								</Button>
							)}
							{deletedUpload?.id && (
								<RestoreItemButton
									target={softDeleteTargets.dataUpload(deletedUpload.id)}
									name={deletedUpload.name || t('common.entities.file', { count: 1 })}
									restoreRole={ROLE.DATA_UPLOADS_DELETE}
									onRestored={() => {
										doRefresh?.();
										onOpenChange(false);
									}}
								/>
							)}
							<Restricted to={ROLE.DATA_UPLOADS_DELETE}>
								<DeleteButton onClick={() => setShowDeleteAlert(true)} />
							</Restricted>
						</div>
						<div className="flex gap-2">
							<Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
								{t('common.actions.cancel')}
							</Button>
							<Restricted to={ROLE.DATA_UPLOADS_UPDATE}>
								<Button size="sm" onClick={onSave}>
									{t('files.preview.saveChanges')}
								</Button>
							</Restricted>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			{dataUpload?.id && (
				<DeleteConfirmDialog
					open={showDeleteAlert}
					onOpenChange={setShowDeleteAlert}
					target={softDeleteTargets.dataUpload(dataUpload.id)}
					name={fileName}
					isDeleting={deleteDataUploadApi.isPending}
					onConfirm={onDelete}
				/>
			)}
		</ReadOnlySubtree>
	);
}
